// app/javascript/controllers/message_form_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = [
        "textInput",
        "audioRecorder",
        "audioInput",
        "recordingTimer",
        "waveform",
        "mediaPreview",
        "emojiPicker"
    ]

    static values = {
        conversationId: String
    }

    connect() {
        this.mediaRecorder = null
        this.audioChunks = []
        this.recordingStartTime = null
        this.recordingInterval = null
        this.isRecording = false
    }

    // Text input handling
    handleKeydown(event) {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault()
            this.element.requestSubmit()
        }
    }

    adjustHeight() {
        const textarea = this.textInputTarget
        textarea.style.height = 'auto'
        textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px'
    }

    // Emoji functionality
    toggleEmojiPicker() {
        this.emojiPickerTarget.classList.toggle('hidden')
    }

    insertEmoji(event) {
        const emoji = event.target.dataset.emoji
        const textarea = this.textInputTarget
        const start = textarea.selectionStart
        const end = textarea.selectionEnd
        const text = textarea.value

        textarea.value = text.substring(0, start) + emoji + text.substring(end)
        textarea.focus()
        textarea.selectionStart = textarea.selectionEnd = start + emoji.length

        // Picker stays open so several emojis can be chosen in a row
        this.adjustHeight()
    }

    // Media handling - unified for images and videos
    previewMedia(event) {
        const files = Array.from(event.target.files)
        const previewContainer = this.mediaPreviewTarget
        previewContainer.classList.remove('hidden')

        // Clear previous previews
        previewContainer.innerHTML = ''

        files.forEach(file => {
            const previewElement = this.createMediaPreview(file)
            if (previewElement) {
                previewContainer.appendChild(previewElement)
            }
        })
    }

    createMediaPreview(file) {
        const wrapper = document.createElement('div')
        wrapper.className = 'relative'

        const removeButton = document.createElement('button')
        removeButton.type = 'button'
        removeButton.className = 'absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center'
        removeButton.innerHTML = '×'
        removeButton.addEventListener('click', (e) => {
            e.preventDefault()
            wrapper.remove()
            if (this.mediaPreviewTarget.children.length === 0) {
                this.mediaPreviewTarget.classList.add('hidden')
            }
        })

        if (file.type.startsWith('image/')) {
            const img = document.createElement('img')
            img.src = URL.createObjectURL(file)
            img.className = 'w-20 h-20 object-cover rounded-lg'
            wrapper.appendChild(img)
            wrapper.appendChild(removeButton)
            return wrapper

        } else if (file.type.startsWith('video/')) {
            const video = document.createElement('video')
            video.src = URL.createObjectURL(file)
            video.className = 'w-20 h-20 object-cover rounded-lg'
            video.controls = false
            video.muted = true
            wrapper.appendChild(video)
            wrapper.appendChild(removeButton)

            // Auto-play video for preview (muted)
            video.play().catch(e => console.log('Auto-play prevented:', e))
            return wrapper
        }

        return null
    }

    removeMedia(event) {
        event.target.closest('.relative').remove()
        if (this.mediaPreviewTarget.children.length === 0) {
            this.mediaPreviewTarget.classList.add('hidden')
        }
    }

    // Audio recording functionality
    async startRecording() {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
            this.mediaRecorder = new MediaRecorder(stream)
            this.audioChunks = []

            this.mediaRecorder.ondataavailable = (event) => {
                this.audioChunks.push(event.data)
            }

            this.mediaRecorder.onstop = () => {
                const audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' })
                const file = new File([audioBlob], 'voice-message.webm', { type: 'audio/webm' })

                const dataTransfer = new DataTransfer()
                dataTransfer.items.add(file)
                this.audioInputTarget.files = dataTransfer.files
            }

            this.mediaRecorder.start()
            this.isRecording = true
            this.audioRecorderTarget.classList.remove('hidden')
            this.startRecordingTimer()

            // Simple waveform simulation
            this.simulateWaveform()

        } catch (error) {
            console.error('Error accessing microphone:', error)
            alert('Impossible d\'accéder au microphone. Veuillez vérifier les permissions.')
        }
    }

    stopRecording() {
        if (this.mediaRecorder && this.isRecording) {
            this.mediaRecorder.stop()
            this.mediaRecorder.stream.getTracks().forEach(track => track.stop())
            this.isRecording = false
            this.stopRecordingTimer()
        }
    }

    // New method: Cancel recording without sending
    cancelRecording() {
        this.stopRecording()
        // Reset the audio input
        this.audioInputTarget.value = ''
        this.audioChunks = []
        // Hide the audio recorder interface
        this.audioRecorderTarget.classList.add('hidden')
    }

    sendAudio() {
        this.stopRecording()
        // Submit the form with the audio file
        setTimeout(() => {
            this.element.requestSubmit()
        }, 100)
    }

    startRecordingTimer() {
        this.recordingStartTime = Date.now()
        this.recordingInterval = setInterval(() => {
            const elapsed = Math.floor((Date.now() - this.recordingStartTime) / 1000)
            const minutes = Math.floor(elapsed / 60).toString().padStart(2, '0')
            const seconds = (elapsed % 60).toString().padStart(2, '0')
            this.recordingTimerTarget.textContent = `${minutes}:${seconds}`
        }, 1000)
    }

    stopRecordingTimer() {
        if (this.recordingInterval) {
            clearInterval(this.recordingInterval)
            this.recordingInterval = null
        }
    }

    simulateWaveform() {
        const waveform = this.waveformTarget
        waveform.innerHTML = ''

        const bars = 20
        for (let i = 0; i < bars; i++) {
            const bar = document.createElement('div')
            bar.className = 'w-1 bg-sky-500 mx-px transition-all duration-300'
            bar.style.height = '4px'
            waveform.appendChild(bar)
        }

        this.waveformInterval = setInterval(() => {
            if (!this.isRecording) {
                clearInterval(this.waveformInterval)
                return
            }

            Array.from(waveform.children).forEach(bar => {
                const randomHeight = Math.floor(Math.random() * 20) + 4
                bar.style.height = `${randomHeight}px`
            })
        }, 200)
    }

    resetForm() {
        this.textInputTarget.value = ''
        this.textInputTarget.style.height = 'auto'
        this.mediaPreviewTarget.classList.add('hidden')
        this.mediaPreviewTarget.innerHTML = ''
        this.emojiPickerTarget.classList.add('hidden')
        this.audioRecorderTarget.classList.add('hidden')

        // Reset file inputs
        this.element.querySelectorAll('input[type="file"]').forEach(input => {
            input.value = ''
        })

        // Reset recording state
        this.audioChunks = []
        this.isRecording = false
    }

    disconnect() {
        this.stopRecording()
        if (this.waveformInterval) {
            clearInterval(this.waveformInterval)
        }
    }
}